// Policy Service - Real API integration for OD Policy management
// Connects to backend Express API for policy operations

import { apiClient } from './apiClient';
import { ServiceError } from './ServiceError';

interface assignment {
  groups: [],
  totalAssignments: number,
  users: []
}

export interface Policy {
  id: string;
  name: string;
  status: 'Active' | 'Pending' | 'Inactive';
  assignments?: assignment, 
  precedenceRank: number;
  managementMode: 'Actively Managed' | 'Fully Delegated' | 'Teams Managed' | 'ADM Managed' | 'Not Managed';
  syncWithFeed: boolean;
  userGroup: string;
  entraGroupId?: string;
  groupId?: string;  // Added for group assignment (matches database column)
  bilateralChat: boolean;
  // Fields returned by the single-policy endpoint (GET /policies/:id)
  federatedBilateralChats?: boolean;
  FederatedBilateralChats?: boolean;
  enableFederationAccess?: boolean;
  EnableFederationAccess?: boolean;
  allowFederatedUsers?: boolean;
  includedDomains: string[];
  allowedDomains?: string[];  // Backend returns this field
  customDomains: string[];
  excludedDomains: string[];
  blockedDomains?: string[];  // Backend returns this field
  associatedTeamsPolicies: string[];
  lastSyncWithTeams: string;
  lastFeedSync: string;
  syncStatus: 'In Sync' | 'Drift Detected' | 'Never Synced' | 'Sync Error';
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  // New drift-related fields
  driftStatus?: 'in_sync' | 'drift_detected' | 'drift_declined';
  assignedGroupId?: string;
  assignedGroupDisplayName?: string;
  rank?: number;
  fingerprint?: any;
  // Teams policy attributes from PowerShell
  teamsPolicyDetails?: {
    enableFederationAccess?: boolean;
    enablePublicCloudAccess?: boolean;
    enablePublicCloudAudioVideoAccess?: boolean;
    enableOutsideAccess?: boolean;
    enableTeamsConsumer?: boolean;
    enableTeamsConsumerInbound?: boolean;
    enableTeamsSMS?: boolean;
    enableXMPPAccess?: boolean;
    restrictTeamsConsumerToExternalUserProfiles?: boolean;
    communicationWithExternalOrgs?: string;
    allowedExternalDomains?: string[];
    blockedExternalDomains?: string[];
  };
  // New federation configuration from backend API
  federationConfig?: {
    enableFederationAccess: boolean;
    communicationWithExternalOrgs: string;
    allowedExternalDomains: string[];
    blockedExternalDomains: string[];
    enablePublicCloudAccess: boolean;
    enablePublicCloudAudioVideoAccess: boolean;
    enableTeamsConsumerAccess: boolean;
    enableTeamsConsumerInbound: boolean;
    enableOutsideAccess: boolean;
    enableXmppAccess: boolean;
    enableAcsFederationAccess: boolean;
    enableTeamsSmsAccess: boolean;
    restrictTeamsConsumerToExternalUserProfiles: boolean;
  };
  // Business logic flag from backend
  readyForOnboarding?: boolean;
  onboardingBlockers?: string[];
  // Inherited domain protection
  inherited_domains?: string[];
}

export interface PolicyWithSyncHistory {
  data: {
    policies: Policy[];
    count: number;
  };
  source: string;
  success: boolean;
  syncStatus: string;
  syncHistory: {
    policy: {
      inserted: number;
      updated: number;
      deleted: number;
      created_at: string;
    };
    assignment: {
      inserted: number;
      updated: number;
      deleted: number;
      created_at: string;
    };
  };
  timestamp: string;
}

export interface CreatePolicyRequest {
  name: string;
  userGroup: string;
  entraGroupId?: string;  // Legacy single group support
  groupDisplayName?: string;  // Legacy single group display name
  groups?: Array<{  // New: support for multiple groups
    groupId: string;
    displayName: string;
    rank?: number;
  }>;
  users?: Array<{  // New: support for multiple users
    userId: string;
    userPrincipalName: string;
    displayName: string;
    rank?: number;
  }>;
  bilateralChat: boolean;
  enableFederationAccess?: boolean;
  managementMode?: 'Actively Managed' | 'Fully Delegated' | 'Teams Managed' | 'ADM Managed' | 'Not Managed';
  customDomains?: string[];
}

export interface PolicySyncResult {
  policyId: string;
  driftReport: {
    hasDrift: boolean;
    differences: Array<{
      setting: string;
      odValue: any;
      teamsValue: any;
      status: 'drift' | 'match' | 'missing';
    }>;
  };
  syncStatus: string;
  lastSync: string;
}

export interface BulkSyncResult {
  syncResults: Array<{
    policyId: string;
    policyName: string;
    hasDrift: boolean;
    driftReport: any;
  }>;
  totalPolicies: number;
  policiesWithDrift: number;
  timestamp: string;
}

class PolicyService {

  // Get all polices with syncHistory
  async getPoliciesWithSyncHistory(): Promise<PolicyWithSyncHistory> {
    try {
      const response = await apiClient.get<{
        policies: Policy[];
        count: number;
      }>('/policies');
      
      // ApiResponse now supports additional fields via index signature
      // Backend returns these fields at root level
      return {
        data: response.data,
        syncHistory: response.syncHistory,
        source: response.source,
        success: response.success,
        syncStatus: response.syncStatus,
        timestamp: response.timestamp
      };
    } catch (error) {
      console.error('Failed to fetch policies:', error);
      if (error instanceof ServiceError) {
        throw error;
      }
      throw new ServiceError('Failed to load policies. Please try again.', {
        code: 'POLICY_LOAD_FAILED',
        cause: error as Error
      });
    }
  }

  // Get all policies
  async getPolicies(): Promise<Policy[]> {
    try {
      const response = await apiClient.get<{
        policies: Policy[];
        count: number;
      }>('/policies');

      return this.extractPoliciesFromResponse(response);
    } catch (error) {
      console.error('Failed to fetch policies:', error);
      if (error instanceof ServiceError) {
        throw error;
      }
      throw new ServiceError('Failed to load policies. Please try again.', {
        code: 'POLICY_LOAD_FAILED',
        cause: error as Error
      });
    }
  }

  async getPolicyById(id: string): Promise<Policy | undefined> {
    const response = await apiClient.get<Policy>(`/policies/${encodeURIComponent(id)}`);
    if (!response?.data) return undefined;
    return response.data;
  }

  async updatePolicySettings(
    policyId: string,
    patch: { enableFederationAccess?: boolean; bilateralChat?: boolean },
  ): Promise<void> {
    await apiClient.patch(`/policies/${encodeURIComponent(policyId)}`, patch);
  }

  // Helper method to extract policies from different response formats
  private extractPoliciesFromResponse(response: any): Policy[] {
    if (!response) {
      return [];
    }

    // Check if data has policies array (expected format)
    if (this.hasPoliciesArray(response.data)) {
      return response.data.policies;
    }

    // Check if data itself is an array (fallback format)
    if (Array.isArray(response.data)) {
      return response.data as Policy[];
    }

    // Handle raw response format (direct Teams API)
    if (this.hasRawPoliciesArray(response)) {
      return (response as any).policies;
    }

    return [];
  }

  // Helper method to check if response data has policies array
  private hasPoliciesArray(data: any): boolean {
    return data && data.policies && Array.isArray(data.policies);
  }

  // Helper method to check if response has raw policies array
  private hasRawPoliciesArray(response: any): boolean {
    return response && (response as any).policies && Array.isArray((response as any).policies);
  }

  // Create a new policy
  async createPolicy(policyData: CreatePolicyRequest): Promise<{
    odPolicy: Policy;
    teamsPolicy: {
      id: string;
      created: boolean;
      verified: boolean;
    };
    message: string;
  }> {
    try {
      console.log('📤 policyService.createPolicy received:', JSON.stringify(policyData, null, 2));

      // Map frontend fields to backend expected format
      // Surely its easier to just send the CreatePolicyRequest directly??? Why else do we have it??
      const backendRequest = {
        name: policyData.name,
        userGroup: policyData.userGroup,
        groupId: policyData.entraGroupId, // Legacy single group support
        groupDisplayName: policyData.groupDisplayName || policyData.userGroup, // Legacy single group display name
        groups: policyData.groups || [], // Send groups array if provided
        users: policyData.users || [], // Send users array if provided
        allowedDomains: policyData.customDomains || [],
        blockedDomains: [], // Not in frontend interface, default to empty
        bilateralChat: policyData.bilateralChat,
        enableFederationAccess: policyData.enableFederationAccess,
        rank: 5 // Default rank
      };

      console.log('🌐 Sending to backend API:', JSON.stringify(backendRequest, null, 2));
      console.log('🌐 Backend request details:', {
        name: backendRequest.name,
        groupsCount: backendRequest.groups.length,
        usersCount: backendRequest.users.length,
        groups: backendRequest.groups,
        users: backendRequest.users
      });

      const response = await apiClient.post<{
        odPolicy: Policy;
        teamsPolicy: {
          id: string;
          created: boolean;
          verified: boolean;
        };
        message: string;
      }>('/policies', backendRequest);

      return response.data;
    } catch (error) {
      console.error('Failed to create policy:', error);
      if (error instanceof ServiceError) {
        throw error;
      }
      throw new ServiceError('Failed to create policy. Please try again.', {
        code: 'POLICY_CREATE_FAILED',
        cause: error as Error
      });
    }
  }

  // Sync a specific policy with Teams Admin Center
  async syncPolicy(policyId: string): Promise<PolicySyncResult> {
    try {
      const response = await apiClient.post<PolicySyncResult>(`/policies/${policyId}/sync`);
      return response.data;
    } catch (error) {
      console.error(`Failed to sync policy ${policyId}:`, error);
      if (error instanceof ServiceError) {
        throw error;
      }
      throw new ServiceError('Failed to sync policy. Please try again.', {
        code: 'POLICY_SYNC_FAILED',
        details: { policyId },
        cause: error as Error
      });
    }
  }

  // Get Teams policies associated with an OD policy
  async getTeamsPolicies(policyId: string): Promise<{
    odPolicyId: string;
    teamsPolicies: any[];
    count: number;
  }> {
    try {
      const response = await apiClient.get<{
        odPolicyId: string;
        teamsPolicies: any[];
        count: number;
      }>(`/policies/${policyId}/teams-policies`);

      return response.data;
    } catch (error) {
      console.error(`Failed to get Teams policies for ${policyId}:`, error);
      if (error instanceof ServiceError) {
        throw error;
      }
      throw new ServiceError('Failed to load Teams policies. Please try again.', {
        code: 'TEAMS_POLICIES_LOAD_FAILED',
        details: { policyId },
        cause: error as Error
      });
    }
  }

  // Bulk sync all policies
  async syncAllPolicies(): Promise<BulkSyncResult> {
    try {
      const response = await apiClient.post<BulkSyncResult>('/policies/sync-all-teams', {});
      return response.data;
    } catch (error) {
      console.error('Failed to bulk sync policies:', error);
      if (error instanceof ServiceError) {
        throw error;
      }
      throw new ServiceError('Failed to sync all policies. Please try again.', {
        code: 'BULK_SYNC_FAILED',
        cause: error as Error
      });
    }
  }

  // Calculate derived properties for display
  getDomainsCount(policy: Policy): number {
    // Ensure includedDomains is an array and return its length
    return Array.isArray(policy.includedDomains) ? policy.includedDomains.length : 0;
  }

  // Get last updated time for display
  getLastUpdatedDisplay(lastSyncWithTeams: string): string {
    const lastSync = new Date(lastSyncWithTeams);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - lastSync.getTime()) / 60000);

    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
    } else if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else {
      const days = Math.floor(diffInMinutes / 1440);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    }
  }

  // Check if policy has Feed sync enabled
  hasFeedSync(policy: Policy): boolean {
    return policy.syncWithFeed;
  }

  // Get sync status display
  getSyncStatusDisplay(policy: Policy): string {
    if (policy.syncWithFeed) {
      return '🔄 Auto-sync enabled';
    } else {
      return '⚠️ Sync disabled';
    }
  }

  // Get detailed Teams policy data
  async getTeamsPolicyDetails(policyName: string): Promise<any> {
    try {
      const response = await apiClient.get(`/policies/${encodeURIComponent(policyName)}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get Teams policy details:', error);
      return null;
    }
  }

  // Import existing Teams policies into OD policies
  async importFromTeams(): Promise<{ imported: Policy[]; count: number; message: string }> {
    try {
      // Sync policies from Teams to database
      const response = await apiClient.post<{
        imported: Policy[];
        count: number;
        message: string;
      }>('/policies/sync', {});

      return response.data;
    } catch (error) {
      console.error('Failed to sync policies from Teams:', error);
      if (error instanceof ServiceError) {
        throw error;
      }
      throw new ServiceError('Failed to sync policies from Teams. Please try again.', {
        code: 'TEAMS_SYNC_FAILED',
        cause: error as Error
      });
    }
  }

  // Check if it's a UUID (contains dashes and is 36 chars)
  private isUuid(value: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
  }

  // Helper method to resolve UUID to policy name
  private async resolvePolicyName(policyIdOrName: string): Promise<string> {
    if (!this.isUuid(policyIdOrName)) {
      return policyIdOrName;
    }

    try {
      const policyResponse = await apiClient.get(`/policies/${policyIdOrName}`);
      if (policyResponse.success && policyResponse.data) {
        const resolvedName = (policyResponse.data as any).name || 
                            (policyResponse.data as any).policy_name || 
                            policyIdOrName;
        console.log(`📝 Resolved UUID ${policyIdOrName} to policy name: ${resolvedName}`);
        return resolvedName;
      }
    } catch (err) {
      console.warn('⚠️ Could not resolve UUID to policy name, will try with UUID:', err);
    }
    
    return policyIdOrName;
  }

  // Helper method to create standardized error responses
  private createErrorResponse(error: unknown, policyIdOrName: string): {
    success: boolean;
    error: string;
  } {
    console.error(`❌ Policy deletion error for ${policyIdOrName}:`, error);
    
    let errorMessage: string;
    if (error instanceof ServiceError) {
      errorMessage = error.message;
    } else if (error instanceof Error) {
      errorMessage = error.message;
    } else {
      errorMessage = 'Unknown error occurred';
    }
    
    return {
      success: false,
      error: `Failed to delete policy: ${errorMessage}`
    };
  }

  // Helper method to handle API response
  private handleDeletionResponse(response: any, policyName: string): {
    success: boolean;
    data?: any;
    error?: string;
  } {
    if (response.success) {
      return {
        success: true,
        data: response.data as any
      };
    }
    
    console.error(`❌ Failed to delete policy ${policyName}:`, response);
    return {
      success: false,
      error: response.message || 'Failed to delete policy'
    };
  }

  // Delete a policy (removes both OD policy and associated Teams policy)
  async deletePolicy(policyIdOrName: string): Promise<{
    success: boolean;
    data?: {
      message: string;
      deletionDetails?: any;
      policyId?: string;
      policyName?: string;
    };
    error?: string | { message: string };
  }> {
    try {
      // For delete operations, we need the policy name, not UUID
      // First, try to get the policy details to find the name
      const policyName = await this.resolvePolicyName(policyIdOrName);
      // Use the async delete endpoint - this queues the deletion and returns immediately
      const response = await apiClient.delete(`/policies/${encodeURIComponent(policyName)}`);
      return this.handleDeletionResponse(response, policyName);
    } catch (error) {
      return this.createErrorResponse(error, policyIdOrName);
    }
  }

}

export const policyService = new PolicyService();
